/* ==========================================================================
   Campaign Direct - calculator composition
   Build theme 152943952070 only. Styling: cd-custom.css rules 5 and 8.
   Loaded by blocks/cd-order-flow.liquid.

   Three jobs, in this order:
     A. THOUSAND SEPARATORS on the displayed figures (23 Aug)
     B. PROMOTE THE TOTAL to the headline slot (22 Aug), behind a guard
     C. SUPPRESS THE PRICE WHEN THE QUANTITY IS OUT OF RANGE (26 Aug)

   == A. THOUSAND SEPARATORS ==============================================

   Apippa renders `$ 1000.00`, not `$ 1,000.00`. Greg has chased this with
   them and been told it is a caching problem.

   WHAT IS TRUE, checked 23 Aug. Their bundle loads from
     https://cdn-assets.custompricecalculator.com/shopify/calculator.js?shop=...
   an UNVERSIONED filename - the only query parameter is the shop, which never
   changes, so the URL is byte-identical across every release they ship. That
   is exactly the setup where a browser can serve a stale copy, so the cache
   story is structurally possible. It is NOT checkable from here: the CDN sends
   no CORS header, so the bundle cannot be fetched and diffed from page script.
   And our `?v=` cache-busting does nothing to it - that busts Shopify asset
   URLs, not a third-party CDN URL.

   Either way this file no longer depends on the answer. We format the display
   ourselves and it holds whatever Apippa ships.

   WHAT WE FORMAT, AND WHAT WE MUST NOT TOUCH. The figures on screen are plain
   <label> elements. The value that can actually reach the cart is a SEPARATE
   hidden input:

     <input type="hidden" name="properties[Total]" value="1000.00">   <- money
     <label name="properties[Total]" data-val="1000.00">1000.00</label> <- pixels

   A <label> is never submitted, so rewriting its text cannot change a price.
   NEVER touch the hidden input, `data-val`, `data-origmoney`, or any <input>.
   If a future edit starts formatting inputs, a comma in a number field parses
   as a truncated number and the order mis-prices. Labels only.

   THE QUANTITY BOX CANNOT HAVE COMMAS AND THIS IS NOT AN APIPPA FAILING.
   Those are <input type="number">. The HTML spec allows only a valid floating
   point number in the value, so a browser rejects `2,500` outright. No app
   release can put a separator there without changing the field type, which
   would break their own parsing. Do not try it here either.

   Formatting is refused unless the label holds a BARE NUMBER. Anything else -
   a word, a range, an empty render mid-update - is left exactly as found. The
   failure mode is an unformatted number, never a wrong one.

   == B. THE PROMOTION GUARD ==============================================

   `#calcElementPriceTicker` IS NOT ALWAYS A PRICE. It is whatever Apippa named
   the first calculation element, and on STICKERS that is `Total Stickers` - a
   display-only count, because Stickers prices per ROLL and has no money Total.

   Without the guard this script rendered, live, for about a day:

       TOTAL STICKERS
       500 stickers          <- a count, in the 34px gold headline slot
       PRICE PER PIECE
       164.00                <- the price of ONE ROLL, captioned per piece

   So: promote the row ONLY when its own label is exactly "Total". Anything
   else and step B does nothing - no move, no relabel - and the calculator
   renders as Apippa drew it, which is correct for Stickers. Steps A and C
   still run.

   If a future product needs promoting and its label is not the bare word
   "Total", rename the element in Apippa rather than loosening this test. A
   loose test here silently mislabels money.

   WHY MOVE RATHER THAN MIRROR. Copying the value into a new element would mean
   keeping two numbers in sync, and a price display that can disagree with
   itself is worse than an ugly one. Moving the real node means Apippa goes on
   updating the same element it always did.

   == C. OUT-OF-RANGE QUANTITIES ==========================================

   Added 26 Aug after the pricing audit. See
   claude/pricing-audit-2026-08-26.md section 2.

   Apippa's formula is `quantity * quantity[actualvalue]`, where [actualvalue]
   is the per-piece rate looked up from the range table. BELOW THE TABLE'S
   FLOOR THE LOOKUP RETURNS NOTHING AND THE TERM FALLS BACK TO THE QUANTITY
   ITSELF, so the formula degenerates to quantity x quantity. Measured on
   Flyers, whose minimum is 5,000:

       typed 100    ->  $10,000.00        (100 squared)
       typed 1,000  ->  $1,000,000.00     (1,000 squared)
       typed 2,500  ->  $6,250,000.00     (2,500 squared)

   The recalculation fires on KEYUP, so a customer who wants 1,000 flyers
   watches the total climb through seven figures as they type. Apippa's own
   CheckNumberInputValidity clamps the field back to the minimum ON BLUR, so
   this cannot reach a cart - but it is fully visible while typing, and Flyers'
   variant ladder still advertises 1,000 and 2,500 rungs the calculator will
   not sell, so real customers will take that path.

   So: while any visible calculator number input is outside its own min/max,
   hide the figures and show the limit instead. A number that is wrong by three
   orders of magnitude must never be on screen; "minimum is 5,000" is both
   honest and more useful.

   IT HIDES, IT DOES NOT REWRITE. Blanking the label text would fight Apippa
   for the same node and could stick after the value came back in range. This
   sets display on the marker's own children and removes it again, so the
   figures are untouched and always Apippa's. The note is our own element and
   is removed entirely when the value is valid.

   THE MIN/MAX COME FROM APIPPA'S OWN ATTRIBUTES on the input, so this needs no
   per-product configuration and picks up any range they change.

   FAILURE MODE IS BENIGN. If this script never runs, rule 5 matches nothing
   and the calculator renders as Apippa drew it: correct figures, old
   proportions, no separators - and the quantity-squared display comes back.
   Presentation code must never be able to misstate a price.
   ========================================================================== */

(function () {
  'use strict';

  var PER_PIECE_LABEL = 'Price per piece';
  var PROMOTABLE_LABEL = 'total';   /* see THE PROMOTION GUARD - do not loosen */
  var NOTE_CLASS = 'cd-range-note';

  var observer = null;
  var pending = null;

  /* Returns the separated form of a BARE number, or null if the text is
     anything else. Existing commas are stripped first, so this is idempotent
     and safe to run on every pass. Digits are never altered - only grouped. */
  function grouped(text) {
    var m = /^(-?)(\d+)(\.\d+)?$/.exec(String(text).replace(/,/g, '').trim());
    if (!m) return null;
    return m[1] + m[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (m[3] || '');
  }

  function commas(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* LABELS ONLY. See the warning in the header. */
  function addSeparators(scope) {
    var els = scope.querySelectorAll('label.calculation-element, label#priceTicker');
    Array.prototype.forEach.call(els, function (el) {
      if (el.tagName !== 'LABEL') return;
      var next = grouped(el.textContent);
      if (next !== null && next !== el.textContent) el.textContent = next;
    });
  }

  /* The moved row keeps the inline colours Apippa gave it for the mist panel.
     On the navy panel they are invisible. Clear them so the panel's own colour
     wins - which keeps the Design Tab in charge. */
  function stripInlineColour(el) {
    if (!el || !el.style) return;
    el.style.removeProperty('color');
    el.style.removeProperty('-webkit-text-fill-color');
  }

  /* --- C ---------------------------------------------------------------- */

  /* The first VISIBLE number input that is outside its own min/max, or null.
     Hidden inputs are the other sizes' quantity boxes, which keep whatever was
     last typed into them and are not what the customer is looking at. */
  function outOfRange(scope) {
    var inputs = scope.querySelectorAll('input[type="number"]');
    var bad = null;
    Array.prototype.forEach.call(inputs, function (i) {
      if (bad) return;
      if (!i.getBoundingClientRect().height) return;
      var v = parseFloat(i.value);
      if (isNaN(v)) return;
      var min = i.min === '' ? NaN : parseFloat(i.min);
      var max = i.max === '' ? NaN : parseFloat(i.max);
      if (!isNaN(min) && v < min) bad = { input: i, limit: min, kind: 'min' };
      else if (!isNaN(max) && v > max) bad = { input: i, limit: max, kind: 'max' };
    });
    return bad;
  }

  function noteText(bad) {
    var n = commas(bad.limit);
    if (bad.kind === 'min') {
      return 'Minimum order is ' + n + '. Enter ' + n + ' or more to see your price.';
    }
    return 'Largest online order is ' + n + '. Email orders@thecampaigndirect.com for a quote above that.';
  }

  function applyRange(marker, scope) {
    var bad = outOfRange(scope);
    var note = marker.querySelector('.' + NOTE_CLASS);

    if (!bad) {
      if (note && note.parentNode) note.parentNode.removeChild(note);
      Array.prototype.forEach.call(marker.children, function (el) {
        if (el.classList.contains(NOTE_CLASS)) return;
        el.style.removeProperty('display');
      });
      return;
    }

    if (!note) {
      note = document.createElement('div');
      note.className = NOTE_CLASS;
      note.style.cssText = 'font-size:.95rem;font-weight:600;line-height:1.35;padding:.1rem 0;';
      marker.appendChild(note);
    }
    var next = noteText(bad);
    if (note.textContent !== next) note.textContent = next;

    Array.prototype.forEach.call(marker.children, function (el) {
      if (el === note) return;
      el.style.setProperty('display', 'none', 'important');
    });
  }

  /* --- the pass --------------------------------------------------------- */

  function reshape() {
    var scope = document.querySelector('.cpc-calculator-target') ||
                document.querySelector('.cpc-calculator-placement');
    if (!scope) return;

    /* A - runs on every product, including ones step B declines to touch. */
    addSeparators(scope);

    var marker = document.querySelector('.price-marker');
    if (!marker) return;

    /* B - identify the candidate row and check what it is BEFORE changing
       anything. The relabel used to happen above this point, which is how
       Stickers ended up with a per-roll price captioned "Price per piece". */
    var totalValue = document.querySelector('#calcElementPriceTicker');
    if (!totalValue || !totalValue.closest) { applyRange(marker, scope); return; }

    var row = totalValue.closest('.element');
    if (!row) { applyRange(marker, scope); return; }

    var rowLabel = row.querySelector('label');
    var rowName = rowLabel ? rowLabel.textContent.trim().toLowerCase() : '';
    if (rowName !== PROMOTABLE_LABEL) { applyRange(marker, scope); return; }

    var label = marker.querySelector(':scope > label');
    if (label && label.textContent.trim() !== PER_PIECE_LABEL) {
      label.textContent = PER_PIECE_LABEL;
    }

    if (row.parentElement !== marker) {
      /* If Apippa re-rendered only the upper block it will have built a fresh
         Total row while ours is still in the navy panel. Drop the stale one
         rather than ending up with two totals side by side. */
      Array.prototype.forEach.call(marker.querySelectorAll('.cd-total-row'), function (old) {
        if (old !== row && old.parentNode) old.parentNode.removeChild(old);
      });
      marker.insertBefore(row, marker.firstChild);
    }
    row.classList.add('cd-total-row');

    stripInlineColour(row);
    Array.prototype.forEach.call(row.querySelectorAll('*'), stripInlineColour);

    /* C - last, so it hides the finished composition rather than racing it. */
    applyRange(marker, scope);
  }

  /* Apippa redraws the calculator on a size change and again on its own
     schedule, which throws all of the above away. Re-apply on any mutation,
     debounced, with the observer DISCONNECTED while we work - that is what
     stops our own text rewrites from feeding back as new mutations. */
  function apply() {
    if (observer) observer.disconnect();
    try { reshape(); } finally { observe(); }
  }

  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(apply, 60);
  }

  function observe() {
    var target = document.querySelector('.cpc-calculator-target') ||
                 document.querySelector('.cpc-calculator-placement') ||
                 document.body;
    if (!observer) observer = new MutationObserver(schedule);
    observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  }

  /* A MutationObserver does not fire on typing, because a value change is not
     a DOM mutation. Job C has to see keystrokes, so it also listens on input. */
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t && t.tagName === 'INPUT' && t.type === 'number' && t.closest &&
        (t.closest('.cpc-calculator-target') || t.closest('.cpc-calculator-placement'))) {
      schedule();
    }
  }, true);

  function boot() {
    apply();
    /* The calculator draws about a second after load, and the observer above is
       attached to whatever existed at boot. Re-attach once it is really there.
       Polls until the panel exists or 10s passes - the timeout is also the
       normal path on a product step B declines to promote. */
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      if (document.querySelector('.price-marker .cd-total-row') || tries > 40) {
        clearInterval(poll);
        apply();
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
